import {
  PlayerCardReview,
  PlayerCardReviewContent,
  PublicReviewWidgetReview,
  Review,
  ReviewOverviewSnapshot,
  ReviewSourceSystem,
} from "@/services/bedrock";
import { positiveSentimentPercent } from "@/containers/Coach/Reputation/Overview/snapshotHelpers";
import { DUMMY_REVIEWS } from "@/containers/Coach/Reputation/Showcase/components/sampleReviews";
import { MIN_REVIEW_RESPONSES, REVIEW_RATING_THRESHOLDS } from "./utils";

const FEATURED_REVIEW_LIMIT = 9;
const PREVIEW_REVIEW_PAGE_URL = "#";
const NAME_SUFFIXES = ["jr", "jr.", "sr", "sr.", "ii", "iii", "iv"];

const RATING_SUMMARIES = {
  STAR_RATING: {
    kind: "star",
    rating: (snapshot: ReviewOverviewSnapshot) => snapshot.avgStarRating,
    totalResponses: (snapshot: ReviewOverviewSnapshot) => snapshot.totalStarReviews,
  },
  NPS_RATING: {
    kind: "nps",
    rating: (snapshot: ReviewOverviewSnapshot) => snapshot.npsNetScore,
    totalResponses: (snapshot: ReviewOverviewSnapshot) => snapshot.totalNpsResponses,
  },
  SATISFACTION_RATING: {
    kind: "sentiment",
    rating: (snapshot: ReviewOverviewSnapshot) => Math.round((positiveSentimentPercent(snapshot) ?? 0) * 10) / 10,
    totalResponses: (snapshot: ReviewOverviewSnapshot) => sentimentTotal(snapshot),
  },
} as const;

function sentimentTotal(snapshot: ReviewOverviewSnapshot) {
  return (
    (snapshot.sentimentPositiveCount ?? 0) +
    (snapshot.sentimentNeutralCount ?? 0) +
    (snapshot.sentimentNegativeCount ?? 0)
  );
}

const isPubliclyVisible = (review: Review) =>
  review.active && review.status === "published" && review.approvedForSocial === true;

const publicDisplayName = (name: string | null) => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "Anonymous";
  while (parts.length > 1 && NAME_SUFFIXES.includes(parts[parts.length - 1].toLowerCase())) parts.pop();
  const first = /\p{Ll}/u.test(parts[0])
    ? parts[0]
    : parts[0].toLowerCase().replace(/(^|['-])\p{L}/gu, letter => letter.toUpperCase());
  return parts.length === 1 ? first : `${first} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

const toPublicReview = (review: Review): PublicReviewWidgetReview => ({
  content: review.content,
  condensedQuote: review.condensedQuote,
  displayName: publicDisplayName(review.displayName),
  sourceSystem: review.sourceSystem,
  link: review.link,
  ratingType: review.ratingType,
  starRating: review.starRating,
  npsScore: review.npsScore,
  npsCategory: review.npsCategory,
  tenPointRating: review.tenPointRating,
  publishedAt: review.publishedAt,
  sentiment: review.sentiment,
});

export interface ReviewWidgetData {
  featuredReviews: Review[] | undefined;
  snapshot: ReviewOverviewSnapshot | null | undefined;
  cachedRecords: Record<number, Review>;
}

export const buildReviewWidgetContent = (
  owner: Pick<PlayerCardReview, "displayType" | "reviewId" | "sourceSystems">,
  data: ReviewWidgetData
): PlayerCardReviewContent | null | undefined => {
  const withLink = (content: PlayerCardReviewContent) => ({ ...content, reviewPageUrl: PREVIEW_REVIEW_PAGE_URL });

  if (owner.displayType === "FEATURED_REVIEWS") {
    if (!data.featuredReviews) return undefined;
    const sources = owner.sourceSystems ?? [];
    const reviews = data.featuredReviews
      .filter(review => !sources.length || sources.includes(review.sourceSystem))
      .slice(0, FEATURED_REVIEW_LIMIT);
    return reviews.length ? withLink({ reviews: reviews.map(toPublicReview) }) : null;
  }

  if (owner.displayType === "SPECIFIC_REVIEW") {
    if (!owner.reviewId) return null;
    const review = data.cachedRecords[owner.reviewId];
    if (!review) return undefined;
    return isPubliclyVisible(review) ? withLink({ reviews: [toPublicReview(review)] }) : null;
  }

  if (data.snapshot === undefined) return undefined;
  if (data.snapshot === null) return null;
  const summary = RATING_SUMMARIES[owner.displayType];
  const rating = Number(summary.rating(data.snapshot) ?? 0);
  const totalResponses = Number(summary.totalResponses(data.snapshot) ?? 0);
  const minRating = REVIEW_RATING_THRESHOLDS[owner.displayType]?.minRating ?? 0;
  if (totalResponses < MIN_REVIEW_RESPONSES || rating < minRating) return null;
  return withLink({ summary: { [summary.kind]: { rating, totalResponses } } });
};

const TEMPLATE_PREVIEW_CONTENT: PlayerCardReviewContent = {
  reviews: DUMMY_REVIEWS.filter(review => review.approvedForSocial),
  summary: {
    star: { rating: 4.9, totalResponses: 42 },
    nps: { rating: 78, totalResponses: 31 },
    sentiment: { rating: 96, totalResponses: 42 },
  },
  reviewPageUrl: PREVIEW_REVIEW_PAGE_URL,
};

export const templatePreviewContent = (
  owner: Pick<PlayerCardReview, "displayType" | "sourceSystems">
): PlayerCardReviewContent | null => {
  const { reviews = [], summary = {}, reviewPageUrl } = TEMPLATE_PREVIEW_CONTENT;
  switch (owner.displayType) {
    case "FEATURED_REVIEWS": {
      const sources = owner.sourceSystems ?? [];
      const featured = sources.length
        ? reviews.filter(review => sources.includes(review.sourceSystem as ReviewSourceSystem))
        : reviews;
      return featured.length ? { reviews: featured, reviewPageUrl } : null;
    }
    case "SPECIFIC_REVIEW":
      return { reviews: reviews.slice(0, 1), reviewPageUrl };
    case "STAR_RATING":
      return { summary: { star: summary.star }, reviewPageUrl };
    case "NPS_RATING":
      return { summary: { nps: summary.nps }, reviewPageUrl };
    case "SATISFACTION_RATING":
      return { summary: { sentiment: summary.sentiment }, reviewPageUrl };
  }
};
